// Blog Listing Page - SEO Optimized
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SeoHead } from '../components/SeoHead';
import { SEO } from '../config/seo';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Layout } from '../components/Layout';
import { Calendar, Eye, User, ArrowRight } from 'lucide-react';

export function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBlogPosts();
  }, []);

  async function loadBlogPosts() {
    try {
      const response = await api.request('/blog/posts?status=published&limit=50');
      setPosts(response.posts || []);
    } catch (err) {
      console.error('Failed to load blog posts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const schemaOrgWebPage = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "UPost Writing Tips",
    "description": "Expert tips, guides, and strategies for authors. Learn about AI writing tools, screenplay formatting, publishing, and becoming a bestselling author.",
    "url": "https://upost.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "UPost",
      "logo": {
        "@type": "ImageObject",
        "url": "https://upost.com/logo.png"
      }
    }
  };

  return (
    <Layout>
      <SeoHead {...SEO.blog} />

      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
        {/* Hero Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
          <div className="container mx-auto max-w-4xl text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Writing Tips & Guides
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 mb-8">
              Expert tips, guides, and strategies to help you write faster, publish successfully, and become a bestselling author
            </p>
            <div className="flex gap-4 justify-center flex-wrap text-sm">
              <span className="px-4 py-2 bg-white/20 rounded-full">📝 AI Writing</span>
              <span className="px-4 py-2 bg-white/20 rounded-full">🎬 Screenwriting</span>
              <span className="px-4 py-2 bg-white/20 rounded-full">📚 Publishing</span>
              <span className="px-4 py-2 bg-white/20 rounded-full">💡 Productivity</span>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-7xl px-4">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                <p className="mt-4 text-gray-600">Loading articles...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-600">Failed to load blog posts. Please try again later.</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">No blog posts yet. Check back soon!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {posts.map((post) => (
                  <Link key={post.slug} to={`/blog/${post.slug}`}>
                    <Card className="h-full hover:shadow-xl transition-shadow cursor-pointer">
                      {post.featured_image_url && (
                        <img 
                          src={post.featured_image_url} 
                          alt={post.title}
                          className="w-full h-48 object-cover rounded-t-lg"
                        />
                      )}
                      <CardHeader>
                        <CardTitle className="text-xl hover:text-purple-600 transition-colors line-clamp-2">
                          {post.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>
                        
                        {/* Keywords */}
                        {post.keywords && post.keywords.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {post.keywords.slice(0, 3).map((keyword, idx) => (
                              <span 
                                key={idx}
                                className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full"
                              >
                                {keyword}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Meta Info */}
                        <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(post.published_at).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {post.author_name}
                          </span>
                          {post.view_count > 0 && (
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {post.view_count}
                            </span>
                          )}
                        </div>
                        
                        <Button variant="outline" className="w-full group">
                          Read More 
                          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Start Writing?
            </h2>
            <p className="text-xl text-purple-100 mb-8">
              Join hundreds of authors using AI to write faster and publish successfully
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Get Started Free - No Card Required
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
}
